import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listCustomers, createCustomer, updateCustomer, deleteCustomer, restoreCustomer, importCustomers,
  fetchCustomerMeasurements, saveCustomerMeasurements, fetchCustomerInvoices, fetchFamilyMembers, listMeasurements,
} from '../api/resources';
import { useToast } from '../context/ToastContext';
import { inr, fmtDate, initials, assetUrl } from '../utils/format';
import { STATES, CITIES_BY_STATE } from '../utils/india';
import Modal from '../components/ui/Modal';
import Field from '../components/ui/Field';
import { StatusBadge } from '../components/ui/Badge';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import EmptyState from '../components/ui/EmptyState';
import DataTable from '../components/ui/DataTable';
import PhotoUpload from '../components/ui/PhotoUpload';
import DateInput from '../components/ui/DateInput';
import Spinner from '../components/ui/Spinner';
import { ChangeLogHint, ChangeLogModal } from '../components/ui/ChangeLog';
import MeasurementInput from '../components/ui/MeasurementInput';
import { parseMeasurement } from '../utils/fraction';
import PageHeader from '../components/ui/PageHeader';
import { usePortalAccess } from '../hooks/usePortalAccess';
import { Pencil, Plus, ReceiptText, RotateCcw, Ruler, Trash2, Upload, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
// Same new-customer box as the order screen, so both behave alike.
import { QuickAddCustomer } from './InvoiceWizard';

const EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'rediffmail.com'];

export default function Customers() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [form, setForm] = useState(null); // {__new:true} or customer
  const [measurementsFor, setMeasurementsFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [del, setDel] = useState(null);
  const [bulkDel, setBulkDel] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(null); // search text with no match
  const nav = useNavigate();
  // A customer opens the order screen with them already chosen.
  const openOrder = (c) => nav(`/invoices/new?customer=${c.id}`, { state: { from: '/customers' } });

  // DataTable handles search/sort/paging client-side, so pull a full working
  // set rather than paging on the server.
  // Deleting a customer who has orders only moves them out of the way, so the
  // page has two sides and this is which one you are looking at.
  const [trash, setTrash] = useState(false);
  // A portal login may be allowed to look up customers without being allowed
  // to add them, so the buttons follow the same rule the server does.
  const { can } = usePortalAccess();
  const mayAdd = can('createOrder');

  const { data, isLoading } = useQuery({
    queryKey: ['customers', trash ? 'trash' : 'all'],
    queryFn: () => listCustomers({ page: 1, perPage: 1000, ...(trash ? { status: 'INACTIVE' } : {}) }),
  });
  const customers = data?.customers || [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['customers'] });

  const delMut = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: (d) => { success(d.message); invalidate(); setDel(null); },
    onError: (e) => error(e.response?.data?.message || 'Delete failed'),
  });

  const restoreMut = useMutation({
    mutationFn: restoreCustomer,
    onSuccess: (d) => { success(d.message || 'Customer restored.'); invalidate(); },
    onError: (e) => error(e.response?.data?.message || 'Could not restore this customer.'),
  });

  const bulkDelMut = useMutation({
    mutationFn: async (rows) => {
      const results = await Promise.allSettled(rows.map((r) => deleteCustomer(r.id)));
      return { total: rows.length, failed: results.filter((r) => r.status === 'rejected').length };
    },
    onSuccess: ({ total, failed }) => {
      if (failed) error(`${total - failed} deleted, ${failed} could not be deleted.`);
      else success(`${total} customer${total === 1 ? '' : 's'} deleted.`);
      invalidate();
      setBulkDel(null);
    },
    onError: () => error('Bulk delete failed'),
  });

  const trashColumns = [
    {
      key: 'name',
      header: 'Customer',
      hideable: false,
      render: (c) => <span className="font-medium text-content">{c.name}</span>,
    },
    { key: 'mobile', header: 'Mobile', render: (c) => <span className="text-content-muted">{c.mobile}</span> },
    {
      key: 'city',
      header: 'Location',
      exportValue: (c) => [c.city, c.state].filter(Boolean).join(', '),
      render: (c) => <span className="text-content-muted">{[c.city, c.state].filter(Boolean).join(', ') || '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      hideable: false,
      align: 'right',
      exportValue: () => '',
      render: (c) => (
        <button className="btn-outline btn-sm" disabled={restoreMut.isPending} onClick={() => restoreMut.mutate(c.id)}>
          <RotateCcw size={13} /> Restore
        </button>
      ),
    },
  ];

  const columns = [
    {
      key: 'name',
      header: 'Customer',
      hideable: false,
      render: (c) => (
        <div className="flex items-center gap-2.5">
          {c.photo ? (
            <img src={assetUrl(c.photo)} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="chip-brand flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold">
              {initials(c.name)}
            </span>
          )}
          <span className="font-medium text-content">{c.name}</span>
        </div>
      ),
    },
    {
      key: 'mobile',
      header: 'Mobile',
      render: (c) => (
        <a href={`tel:${c.mobile}`} className="font-medium text-brand-600 hover:underline">{c.mobile}</a>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      defaultHidden: true,
      render: (c) => (c.email
        ? <a href={`mailto:${c.email}`} className="text-content-muted hover:underline">{c.email}</a>
        : <span className="text-content-subtle">—</span>),
    },
    {
      key: 'city',
      header: 'Location',
      sortValue: (c) => `${c.city || ''}${c.state || ''}`,
      exportValue: (c) => [c.city, c.state].filter(Boolean).join(', '),
      render: (c) => <span className="text-content-muted">{[c.city, c.state].filter(Boolean).join(', ') || '—'}</span>,
    },
    { key: 'taxType', header: 'Tax Type', render: (c) => <StatusBadge status={c.taxType} /> },
    { key: 'status', header: 'Status', render: (c) => <StatusBadge status={c.status} /> },
    {
      key: 'actions',
      header: '',
      sortable: false,
      hideable: false,
      align: 'right',
      exportValue: () => '',
      render: (c) => (
        <div className="flex items-center justify-end gap-0.5">
          <button className="btn-icon" title="Measurements" onClick={() => setMeasurementsFor(c)}><Ruler size={15} /></button>
          <button className="btn-icon" title="Invoice history" onClick={() => setHistoryFor(c)}><ReceiptText size={15} /></button>
          <button className="btn-icon" title="Edit" onClick={() => setForm(c)}><Pencil size={15} /></button>
          <button className="btn-icon hover:bg-danger-50 hover:text-danger-600" title="Delete" onClick={() => setDel(c)}><Trash2 size={15} /></button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        icon={Users}
        title="Customers"
        subtitle="Click a customer to start an order · no match? type the name and press Enter"
        actions={mayAdd ? (
          <>
            <button
              className={trash ? 'btn-primary' : 'btn-outline'}
              onClick={() => setTrash((v) => !v)}
              title={trash ? 'Back to your customer list' : 'Customers you deleted who had orders'}
            >
              <Trash2 size={15} /> {trash ? 'Back to customers' : 'Trash'}
            </button>
            <button className="btn-outline" onClick={() => setImportOpen(true)}>
              <Upload size={15} /> Import
            </button>
            <button className="btn-primary" onClick={() => setForm({ __new: true })}>
              <Plus size={15} /> Add Customer
            </button>
          </>
        ) : null}
      />

      {trash && (
        <p className="rounded-lg border border-warning-500 bg-warning-50/40 px-3 py-2 text-xs text-content-muted">
          These customers were deleted, but they had orders — so their history was kept and they were moved here.
          Restoring one puts them back in your list, with their mobile number and measurements intact.
        </p>
      )}

      <DataTable
        key={trash ? 'trash' : 'live'}
        columns={trash ? trashColumns : columns}
        rows={customers}
        loading={isLoading}
        storageKey="customers"
        searchPlaceholder="Search name, mobile, city… (Enter adds a new customer)"
        onRowClick={trash ? undefined : openOrder}
        onSearchEnter={trash || !mayAdd ? undefined : (text) => { if (text) setQuickAdd(text); }}
        searchAddLabel={mayAdd ? 'Add' : undefined}
        searchKeys={['name', 'mobile', 'email', 'city']}
        selectable
        exportable
        exportFilename="customers"
        emptyTitle={trash ? 'The trash is empty' : 'No customers yet'}
        emptyHint={trash
          ? 'Customers you delete who have orders will wait here.'
          : 'Add your first customer to start creating orders.'}
        bulkActions={(rows) => (trash ? (
          <button className="btn-primary btn-sm" onClick={() => rows.forEach((r) => restoreMut.mutate(r.id))}>
            <RotateCcw size={13} /> Restore {rows.length}
          </button>
        ) : (
          <button className="btn-danger btn-sm" onClick={() => setBulkDel(rows)}>
            <Trash2 size={13} /> Delete {rows.length}
          </button>
        ))}
      />

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.__new ? 'Add Customer' : 'Edit Customer'} size="lg">
        {form && <CustomerForm customer={form.__new ? null : form} onDone={(created) => { invalidate(); setForm(null); if (created) setMeasurementsFor(created); }} />}
      </Modal>

      {measurementsFor && <MeasurementsModal customer={measurementsFor} onClose={() => setMeasurementsFor(null)} onAdded={() => invalidate()} />}
      {historyFor && <InvoiceHistoryModal customer={historyFor} onClose={() => setHistoryFor(null)} />}
      <QuickAddCustomer
        open={quickAdd != null}
        initial={quickAdd || ''}
        onClose={() => setQuickAdd(null)}
        onCreated={(c) => { setQuickAdd(null); invalidate(); openOrder(c); }}
      />
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={() => { invalidate(); setImportOpen(false); }} />

      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title="Delete Customer"
        message={`Delete "${del?.name}"? If they have orders, their history is kept and they move to the trash, where you can restore them. If they have none, they are removed for good.`}
        busy={delMut.isPending}
        onConfirm={() => delMut.mutate(del.id)}
      />

      <ConfirmDialog
        open={!!bulkDel}
        onClose={() => setBulkDel(null)}
        title={`Delete ${bulkDel?.length || 0} customers`}
        message={`This will permanently delete ${bulkDel?.length || 0} selected customers. This action cannot be undone.`}
        busy={bulkDelMut.isPending}
        onConfirm={() => bulkDelMut.mutate(bulkDel)}
      />
    </div>
  );
}

function CustomerForm({ customer, onDone }) {
  const { success, error } = useToast();
  const [form, setForm] = useState({
    name: customer?.name || '',
    mobile: customer?.mobile || '',
    email: customer?.email || '',
    birthDate: customer?.birthDate ? fmtDate(customer.birthDate) : '',
    anniversary: customer?.anniversary ? fmtDate(customer.anniversary) : '',
    photo: customer?.photo || '',
    addressLine1: customer?.addressLine1 || '',
    addressLine2: customer?.addressLine2 || '',
    country: customer?.country || 'India',
    state: customer?.state || '',
    city: customer?.city || '',
    pin: customer?.pin || '',
    taxType: customer?.taxType || 'CGST_SGST',
  });
  const [errs, setErrs] = useState({});
  const [prompt, setPrompt] = useState(null); // created customer for the "Add Measurements?" prompt

  const cities = CITIES_BY_STATE[form.state] || [];

  const mutate = useMutation({
    mutationFn: (body) => (customer ? updateCustomer(customer.id, body) : createCustomer(body)),
    onSuccess: (d) => {
      success(customer ? d.message : 'Customer created successfully');
      if (!customer) {
        // Post-create flow: prompt to add measurements
        setPrompt(d.customer);
      } else {
        onDone();
      }
    },
    onError: (e) => {
      const msg = e.response?.data?.message || 'Could not save customer.';
      if (msg.toLowerCase().includes('mobile')) setErrs((x) => ({ ...x, mobile: msg }));
      else error(msg);
    },
  });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Customer name is required.';
    if (!/^\d{10}$/.test(form.mobile)) e.mobile = 'Mobile number must contain exactly 10 digits';
    if (!form.country) e.country = 'Country is required.';
    if (!form.state) e.state = 'State is required.';
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    mutate.mutate(form);
  };

  return (
    <div className="space-y-5">
      {/* Basic information */}
      <section>
        <h4 className="mb-2 border-b border-line pb-1 text-xs font-bold uppercase tracking-wide text-brand-500">Basic Information</h4>
        <div className="space-y-3">
          <Field label="Photo"><PhotoUpload value={form.photo} onChange={(photo) => setForm((f) => ({ ...f, photo }))} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Customer Name" required error={errs.name}>
              <input className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label="Mobile" required error={errs.mobile} hint="Exactly 10 digits">
              <input className="input" inputMode="numeric" maxLength={10} value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, '') }))} />
            </Field>
            <Field label="Email">
              <input className="input" list="email-domains" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              <datalist id="email-domains">
                {EMAIL_DOMAINS.map((d) => <option key={d} value={d} />)}
              </datalist>
            </Field>
            <Field label="Birth Date">
              <DateInput value={form.birthDate} onChange={(birthDate) => setForm((f) => ({ ...f, birthDate }))} />
            </Field>
            <Field label="Anniversary">
              <DateInput value={form.anniversary} onChange={(anniversary) => setForm((f) => ({ ...f, anniversary }))} />
            </Field>
          </div>
        </div>
      </section>

      {/* Address */}
      <section>
        <h4 className="mb-2 border-b border-line pb-1 text-xs font-bold uppercase tracking-wide text-brand-500">Address</h4>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Address Line 1"><input className="input" value={form.addressLine1} onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))} /></Field>
            <Field label="Address Line 2"><input className="input" value={form.addressLine2} onChange={(e) => setForm((f) => ({ ...f, addressLine2: e.target.value }))} /></Field>
            <Field label="Country" required error={errs.country}>
              <select className="input" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}>
                <option value="India">India</option>
                <option>United Arab Emirates</option>
                <option>United States</option>
                <option>United Kingdom</option>
              </select>
            </Field>
            <Field label="State" required error={errs.state}>
              <select className="input" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value, city: '' }))}>
                <option value="">Select state</option>
                {STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="City">
              <select className="input" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} disabled={!cities.length}>
                <option value="">{cities.length ? 'Select city' : 'Select state first'}</option>
                {cities.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="PIN Code">
              <input className="input" inputMode="numeric" maxLength={6} value={form.pin} onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, '') }))} />
            </Field>
          </div>
        </div>
      </section>

      {/* Tax settings */}
      <section>
        <h4 className="mb-2 border-b border-line pb-1 text-xs font-bold uppercase tracking-wide text-brand-500">Tax Settings</h4>
        <Field label="Tax Type">
          <div className="flex gap-4">
            {[
              { v: 'CGST_SGST', l: 'CGST + SGST (intra-state)' },
              { v: 'IGST', l: 'IGST (inter-state)' },
            ].map((t) => (
              <label key={t.v} className="flex items-center gap-2 text-sm text-content-muted">
                <input type="radio" className="text-brand-700" checked={form.taxType === t.v} onChange={() => setForm((f) => ({ ...f, taxType: t.v }))} />
                {t.l}
              </label>
            ))}
          </div>
        </Field>
      </section>

      <div className="flex justify-end gap-2 border-t border-line pt-3">
        <button className="btn-outline" onClick={() => onDone()}>Cancel</button>
        <button className="btn-primary" disabled={mutate.isPending} onClick={submit}>
          {mutate.isPending ? 'Saving…' : customer ? 'Update Customer' : 'Save Customer'}
        </button>
      </div>

      {/* Post-create prompt */}
      <Modal open={!!prompt} onClose={() => { setPrompt(null); onDone(); }} title="Add Measurements?" size="sm">
        <p className="text-sm text-content-muted">
          Customer created successfully. Would you like to add measurements for <b>{prompt?.name}</b>?
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn-outline" onClick={() => { setPrompt(null); onDone(); }}>Not Now</button>
          <button className="btn-primary" onClick={() => { const c = prompt; setPrompt(null); onDone(c); }}>Add Measurements</button>
        </div>
      </Modal>
    </div>
  );
}

function MeasurementsModal({ customer, onClose, onAdded }) {
  const { success, error } = useToast();
  const { data: allMeasurements, isLoading: measurementsLoading } = useQuery({ queryKey: ['measurements', ''], queryFn: () => listMeasurements('') });
  const measurements = allMeasurements?.measurements || [];

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filterItem, setFilterItem] = useState('all');
  // Whose numbers are open. Null is the customer's own; a family member is
  // measured separately and neither set should overwrite the other.
  const [person, setPerson] = useState(null);
  const { data: famData } = useQuery({
    queryKey: ['customer-family', customer.id],
    queryFn: () => fetchFamilyMembers(customer.id),
  });
  const family = famData?.familyMembers || [];
  const whoseName = person ? (family.find((f) => f.id === person)?.name || '') : customer.name;
  const [logFor, setLogFor] = useState(null); // row whose full history is open

  useEffect(() => {
    (async () => {
      // Build the grid from the MASTER measurement list, overlaying any
      // values already saved for this customer. A fresh customer has no saved
      // rows, but we still want to show every master measurement to fill in.
      try {
        const res = await fetchCustomerMeasurements(customer.id, person);
        const saved = res.measurements || [];
        const savedMap = new Map(saved.map((s) => [s.measurementId, s]));
        const master = allMeasurements?.measurements || [];
        setRows(master.map((m) => savedMap.get(m.id) || { measurementId: m.id, value: '', notes: '', measurement: m }));
      } catch {
        setRows((allMeasurements?.measurements || []).map((m) => ({ measurementId: m.id, value: '', notes: '', measurement: m })));
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id, allMeasurements, person]);

  const setVal = (id, field, v) => setRows((r) => r.map((x) => (x.measurementId === id ? { ...x, [field]: v } : x)));

  const save = async () => {
    try {
      await saveCustomerMeasurements(
        customer.id,
        rows.map((r) => ({ measurementId: r.measurementId, value: r.value, notes: r.notes })),
        person,
      );
      success(`Measurements saved for ${whoseName}.`);
      onAdded();
      onClose();
    } catch (x) {
      error(x.response?.data?.message || 'Could not save measurements.');
    }
  };

  const shown = rows;

  return (
    <Modal open onClose={onClose} title={`Measurements — ${whoseName}`} size="lg"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={save}>Save All</button>
      </>}>
      {/* One customer, several people. A father and his son are measured
          separately, and the order screen fills in whoever the order is for. */}
      {family.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-lg border border-line bg-surface-sunken px-2.5 py-2">
          <span className="mr-1 text-2xs font-bold uppercase tracking-wider text-content-subtle">Whose</span>
          <button
            type="button"
            className={person ? 'btn-outline btn-sm' : 'btn-primary btn-sm'}
            onClick={() => setPerson(null)}
          >
            {customer.name}
          </button>
          {family.map((f) => (
            <button
              key={f.id}
              type="button"
              className={person === f.id ? 'btn-primary btn-sm' : 'btn-outline btn-sm'}
              onClick={() => setPerson(f.id)}
              title={f.relation || undefined}
            >
              {f.name}
              {f.relation && <span className="ml-1 text-2xs font-normal opacity-70">{f.relation}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="w-56">
          <label className="label">Filter by Item</label>
          <select className="input" value={filterItem} onChange={(e) => setFilterItem(e.target.value)}>
            <option value="all">All Measurements</option>
            {measurements.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </div>

      <p className="mb-2 text-xs text-content-subtle">
        Type <b>40/</b> for 40¼, <b>40//</b> for 40½, <b>40///</b> for 40¾. Enter or Tab moves to the next field.
      </p>

      {!loaded || measurementsLoading ? <Spinner /> : (
        <table className="w-full">
          <thead className="bg-surface-sunken">
            <tr>
              <th className="th">Measurement</th>
              <th className="th w-40">Value</th>
              <th className="th">Notes</th>
              <th className="th w-44">Change Log</th>
            </tr>
          </thead>
          <tbody>
            {(filterItem === 'all' ? shown : shown.filter((r) => r.measurementId === Number(filterItem))).map((r, i) => (
              <tr key={r.measurementId} className="border-t border-line">
                <td className="td font-medium text-content">{r.measurement?.name || '—'}</td>
                <td className="td">
                  {/* Same field as the order screen, so the slash shorthand
                      (40/ 40// 40///) and the ¼ ½ ¾ display work here too.
                      Stored as a plain decimal string. */}
                  <MeasurementInput
                    // The shorthand is explained above the grid, so only the
                    // first field carries the example.
                    placeholder={i === 0 ? 'e.g. 40//' : ''}
                    value={parseMeasurement(r.value)}
                    onChange={(v) => setVal(r.measurementId, 'value', v == null ? '' : String(v))}
                  />
                </td>
                <td className="td">
                  <input className="input" placeholder="Add note" value={r.notes || ''} onChange={(e) => setVal(r.measurementId, 'notes', e.target.value)} />
                </td>
                <td className="td align-middle">
                  <ChangeLogHint history={r.history} onOpen={() => setLogFor(r)} className="!mt-0" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {loaded && !measurementsLoading && measurements.length === 0 && (
        <p className="py-6 text-center text-sm text-content-subtle">No measurements defined yet. Add measurements under Master first.</p>
      )}

      {logFor && (
        <ChangeLogModal
          title={`${logFor.measurement?.name || 'Measurement'} — change log`}
          history={logFor.history}
          onClose={() => setLogFor(null)}
        />
      )}
    </Modal>
  );
}


function InvoiceHistoryModal({ customer, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['customer-invoices', customer.id],
    queryFn: () => fetchCustomerInvoices(customer.id),
  });
  const invoices = data?.invoices || [];

  return (
    <Modal open onClose={onClose} title={`Invoice History — ${data?.customerName || customer.name}`} size="lg">
      {isLoading && <Spinner />}
      {!isLoading && invoices.length === 0 && (
        <EmptyState title="No Invoice History" hint={`No orders found for ${data?.customerName || customer.name}`} />
      )}
      {!isLoading && invoices.length > 0 && (
        <table className="w-full">
          <thead className="bg-surface-sunken">
            <tr><th className="th">Invoice No.</th><th className="th">Date</th><th className="th">Amount</th><th className="th">Status</th></tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="border-t border-line">
                <td className="td font-medium">{i.orderNo}</td>
                <td className="td">{i.date}</td>
                <td className="td">{inr(i.totalAmount)}</td>
                <td className="td"><StatusBadge status={i.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

function ImportModal({ open, onClose, onImported }) {
  const { success, error } = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const parse = (raw) => {
    const lines = raw.trim().split(/\r?\n/).filter(Boolean);
    return lines
      .slice(1) // skip header
      .map((l) => {
        const cols = l.split(',').map((c) => c.trim());
        return { name: cols[0], mobile: cols[1], email: cols[2], city: cols[3], state: cols[4], taxType: cols[5] || 'CGST_SGST' };
      })
      .filter((r) => r.name && r.mobile);
  };

  const doImport = async () => {
    const rows = parse(text);
    if (!rows.length) return error('No valid rows found. Format: name, mobile, email, city, state, taxType');
    setBusy(true);
    try {
      const res = await importCustomers(rows);
      success(res.message);
      onImported();
      setText('');
    } catch (x) {
      error(x.response?.data?.message || 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Import Customers" size="lg"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={doImport}>{busy ? 'Importing…' : 'Import'}</button>
      </>}>
      <p className="mb-2 text-sm text-content-muted">
        Paste CSV rows below (header row optional). Columns: <code className="rounded bg-surface-hover px-1">name, mobile, email, city, state, taxType</code>
      </p>
      <textarea className="input min-h-[220px] font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} placeholder={'name, mobile, email, city, state, taxType\nRohan Das, 9876543210, rohan@gmail.com, Delhi, Delhi, CGST_SGST'} />
    </Modal>
  );
}